// store/modules/talents.js
import api from '@/api/api'

export default {
    namespaced: true,
    state: {
        talents: [],
        currentTalent: null,
        loading: false,
        error: null,
        pagination: {
            currentPage: 1,
            totalPages: 1,
            total: 0,
            limit: 50
        },
        // Add search state
        search: '',
        statusFilter: '',
        recruiterFilter: '',
        // Add sort state
        sortField: '',
        sortOrder: 0
    },
    getters: {
        allTalents: (state) => state.talents,
        currentTalent: (state) => state.currentTalent,
        loading: (state) => state.loading,
        error: (state) => state.error,
        pagination: (state) => state.pagination,
        search: (state) => state.search,
        statusFilter: (state) => state.statusFilter,
        recruiterFilter: (state) => state.recruiterFilter,
        sortField: (state) => state.sortField,
        sortOrder: (state) => state.sortOrder
    },
    mutations: {
        SET_TALENTS(state, talents) {
            state.talents = talents
        },
        SET_CURRENT_TALENT(state, talent) {
            state.currentTalent = talent
        },
        ADD_TALENT(state, talent) {
            state.talents.push(talent)
        },
        SET_LOADING(state, status) {
            state.loading = status
        },
        SET_ERROR(state, error) {
            state.error = error
        },
        CLEAR_ERROR(state) {
            state.error = null
        },
        SET_PAGINATION(state, pagination) {
            state.pagination = pagination;
        },
        SET_PAGINATION_LIMIT(state, limit) {
            state.pagination.limit = limit;
        },
        SET_SEARCH(state, search) {
            state.search = search;
        },
        SET_STATUS_FILTER(state, status) {
            state.statusFilter = status;
        },
        SET_RECRUITER_FILTER(state, recruiterId) {
            state.recruiterFilter = recruiterId;
        },
        SET_SORT_FIELD(state, field) {
            state.sortField = field;
        },
        SET_SORT_ORDER(state, order) {
            state.sortOrder = order;
        },
        REMOVE_TALENT(state, talentId) {
            state.talents = state.talents.filter(talent => talent._id !== talentId);
            // If the deleted talent was the current one, clear it
            if (state.currentTalent && state.currentTalent._id === talentId) {
                state.currentTalent = null;
            }
        }
    },
    actions: {
        // Submit a new talent
        async submitTalent({commit}, formData) {
            try {
                commit('SET_LOADING', true)
                commit('CLEAR_ERROR')

                // Using axios through our api instance with multipart/form-data
                // Reduced timeout since video processing is now async
                const response = await api.post('/talents', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    timeout: 60000 // 1 minute timeout since video processing is now background
                })

                commit('ADD_TALENT', response.data.data)
                return response.data.data
            } catch (error) {
                let errorMessage = 'Failed to submit talent'
                
                // Handle duplicate email error specifically
                if (error.response?.status === 409 || 
                    error.response?.data?.errorCode === 'DUPLICATE_EMAIL' ||
                    error.response?.data?.message?.includes('already exists')) {
                    errorMessage = error.response.data.message || 'Email address already exists'
                }
                // Handle timeout differently since video is now async
                else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
                    errorMessage = 'Upload timed out. Please check your connection and try again.'
                } else if (error.response?.status === 413) {
                    errorMessage = 'File size too large. Please reduce file sizes and try again.'
                } else if (error.response?.data?.message) {
                    errorMessage = error.response.data.message
                }
                
                commit('SET_ERROR', errorMessage)
                throw new Error(errorMessage)
            } finally {
                commit('SET_LOADING', false)
            }
        },

        // Fetch all talents (admin only) - FIXED TO INCLUDE recruiterId
        async fetchTalents({commit, state}, {page, search, status, recruiterId, sortField, sortOrder, limit} = {}) {

            try {
                commit('SET_LOADING', true);
                commit('CLEAR_ERROR');

                // Set search and status if provided
                if (search !== undefined) commit('SET_SEARCH', search);
                if (status !== undefined) commit('SET_STATUS_FILTER', status);
                if (recruiterId !== undefined) commit('SET_RECRUITER_FILTER', recruiterId);
                if (sortField  !== undefined) commit('SET_SORT_FIELD',   sortField);
                if (sortOrder  !== undefined) commit('SET_SORT_ORDER',   sortOrder);
                if (limit !== undefined) commit('SET_PAGINATION_LIMIT', limit);

                // Use provided page or current page
                const currentPage = page || state.pagination.currentPage;

                // Build query params - only include non-empty values
                
                // Force the limit to be included in the params
                const params = {
                    page: currentPage,
                    limit: limit || state.pagination.limit || 50 // Use provided limit, or state limit, or default to 50
                };

                // Only add parameters that have meaningful values
                if (state.search && state.search.trim()) {
                    params.search = state.search.trim();
                }

                if (state.statusFilter) {
                    params.status = state.statusFilter;
                }

                if (state.recruiterFilter && state.recruiterFilter.trim()) {
                    params.recruiterId = state.recruiterFilter.trim();
                }

                if (state.sortField) {
                    params.sortField = state.sortField;
                }

                if (state.sortOrder !== 0) {
                    params.sortOrder = state.sortOrder;
                }

                const response = await api.get('/talents', {params});

                commit('SET_TALENTS', response.data.data);

                // Update pagination state
                commit('SET_PAGINATION', {
                    currentPage: response.data.currentPage,
                    totalPages: response.data.totalPages,
                    total: response.data.total,
                    limit: state.pagination.limit // Preserve the current limit
                });

                return response.data;
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to fetch talents';
                commit('SET_ERROR', errorMessage);
                throw new Error(errorMessage);
            } finally {
                commit('SET_LOADING', false);
            }
        },

        // helper action so your onSort handler can just dispatch this
        async sortTalents({ dispatch }, { sortField, sortOrder }) {
            return dispatch('fetchTalents', { sortField, sortOrder, page: 1 });
        },

        // Add action to change page
        async changePage({dispatch}, page) {
            return dispatch('fetchTalents', {page});
        },

        // Add action for search
        async searchTalents({dispatch}, search) {
            return dispatch('fetchTalents', {search, page: 1}); // Reset to page 1 when searching
        },

        // Add action for filtering by status
        async filterByStatus({dispatch}, status) {
            return dispatch('fetchTalents', {status, page: 1}); // Reset to page 1 when filtering
        },

        // Add action for filtering by recruiter (SUPERADMIN ONLY)
        async filterByRecruiter({dispatch}, recruiterId) {
            return dispatch('fetchTalents', {recruiterId, page: 1}); // Reset to page 1 when filtering
        },

        // Add action for changing rows per page
        async changeRowsPerPage({commit, dispatch}, rows) {
            commit('SET_PAGINATION_LIMIT', rows);
            // Pass the limit explicitly to ensure it's used
            return dispatch('fetchTalents', {page: 1, limit: rows}); 
        },

        // Fetch a single talent by ID
        async fetchTalentById({commit}, id) {
            try {
                commit('SET_LOADING', true)
                commit('CLEAR_ERROR')

                const response = await api.get(`/talents/${id}`)
                commit('SET_CURRENT_TALENT', response.data.data)
                return response.data.data
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to fetch talent'
                commit('SET_ERROR', errorMessage)
                throw new Error(errorMessage)
            } finally {
                commit('SET_LOADING', false)
            }
        },

        // Update talent status (admin only)
        async updateTalentStatus({commit}, {id, status, statusNotes}) {
            try {
                commit('SET_LOADING', true)
                commit('CLEAR_ERROR')

                const response = await api.put(`/talents/${id}/status`, {status, statusNotes})
                commit('SET_CURRENT_TALENT', response.data.data)
                return response.data.data
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to update talent status'
                commit('SET_ERROR', errorMessage)
                throw new Error(errorMessage)
            } finally {
                commit('SET_LOADING', false)
            }
        },
        async updateTalent({commit}, {id, formData}) {
            try {
                commit('SET_LOADING', true);
                commit('CLEAR_ERROR');

                // Using axios through our api instance with multipart/form-data
                const response = await api.put(`/talents/${id}`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });

                commit('SET_CURRENT_TALENT', response.data.data);
                return response.data;
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to update talent';
                commit('SET_ERROR', errorMessage);
                throw new Error(errorMessage);
            } finally {
                commit('SET_LOADING', false);
            }
        },

        // Get file download URL (admin only)
        async getFileDownloadUrl({commit}, {id, filename}) {
            try {
                commit('SET_LOADING', true);
                commit('CLEAR_ERROR');


                const response = await api.get(`/talents/${id}/files/${encodeURIComponent(filename)}`);
                return response.data;
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to get download URL';
                commit('SET_ERROR', errorMessage);
                throw new Error(errorMessage);
            } finally {
                commit('SET_LOADING', false);
            }
        },

        async getProfilePictureUrl({commit}, id) {
            try {
                commit('SET_LOADING', true);
                commit('CLEAR_ERROR');

                const response = await api.get(`/talents/${id}/profile-picture`);
                return response.data;
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to get profile picture URL';
                commit('SET_ERROR', errorMessage);
                throw new Error(errorMessage);
            } finally {
                commit('SET_LOADING', false);
            }
        },

        // Get video introduction URL
        async getVideoIntroductionUrl({commit}, id) {
            try {
                commit('SET_LOADING', true);
                commit('CLEAR_ERROR');

                const response = await api.get(`/talents/${id}/video-introduction`);
                return response.data;
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to get video introduction URL';
                commit('SET_ERROR', errorMessage);
                throw new Error(errorMessage);
            } finally {
                commit('SET_LOADING', false);
            }
        },

        // Get company logo URL
        async getCompanyLogoUrl({commit}, {id, expIndex}) {
            try {
                commit('SET_LOADING', true);
                commit('CLEAR_ERROR');

                const response = await api.get(`/talents/${id}/work-experience/${expIndex}/company-logo`);
                return response.data;
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to get company logo URL';
                commit('SET_ERROR', errorMessage);
                throw new Error(errorMessage);
            } finally {
                commit('SET_LOADING', false);
            }
        },

        // Get school logo URL
        async getSchoolLogoUrl({commit}, {id, eduIndex}) {
            try {
                commit('SET_LOADING', true);
                commit('CLEAR_ERROR');

                const response = await api.get(`/talents/${id}/education/${eduIndex}/school-logo`);
                return response.data;
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to get school logo URL';
                commit('SET_ERROR', errorMessage);
                throw new Error(errorMessage);
            } finally {
                commit('SET_LOADING', false);
            }
        },

        // Delete talent (SUPERADMIN ONLY)
        async deleteTalent({commit}, {id, reason}) {
            try {
                commit('SET_LOADING', true);
                commit('CLEAR_ERROR');

                const payload = reason ? { reason } : {};
                const response = await api.delete(`/talents/${id}`, { data: payload });
                
                // Remove from local state
                commit('REMOVE_TALENT', id);
                
                return response.data;
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to delete talent';
                commit('SET_ERROR', errorMessage);
                throw new Error(errorMessage);
            } finally {
                commit('SET_LOADING', false);
            }
        }
    }
}
